/**
 * Cart Drawer Handler
 * Manages cart drawer toggle, AJAX updates, and global event listeners
 */

(function() {
  'use strict';

  class CartDrawer {
    constructor() {
      this.drawer = null;
      this.cartIcons = [];
      this.drawerTarget = null;
      this.isOpen = false;
      this.isUpdating = false;
      
      this.init();
    }

    /**
     * Initialize the cart drawer
     */
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupElements());
      } else {
        this.setupElements();
      }
    }

    /**
     * Setup DOM elements and event listeners
     */
    setupElements() {
      // Find the cart drawer aside element
      this.drawer = document.querySelector('aside[data-cart-drawer]');
      
      // Find cart icon elements
      const cartIcons = document.querySelectorAll('[data-cart-icon]');
      this.cartIcons = Array.from(cartIcons);
      
      // Find the drawer target content area
      this.drawerTarget = document.querySelector('[data-cart-drawer-target]');

      if (!this.drawer) {
        return;
      }

      if (!this.drawerTarget) {
        return;
      }

      // Attach event listeners to cart icons
      cartIcons.forEach(icon => {
        // Click to open - prevent default navigation when drawer is available.
        // Best practice: don't toggle-close on icon click; close via explicit controls (close button/overlay/Esc).
        // Also preserve standard browser behavior for modifier-click (open in new tab, etc.).
        icon.addEventListener('click', (e) => {
          // Allow modifier clicks to behave like a normal link (open in new tab/window)
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

          e.preventDefault();
          e.stopPropagation();

          if (!this.isOpen) this.openCartDrawer();
        }, true); // Use capture phase to catch early
      });

      // Close drawer when clicking outside or on close button
      this.setupCloseHandlers();

      // Listen for cart updates
      this.setupGlobalListeners();
    }

    /**
     * Setup close handlers for the drawer
     */
    setupCloseHandlers() {
      // Close button
      const closeButton = this.drawer.querySelector('[data-cart-drawer-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => this.closeCartDrawer());
      }

      // Overlay/backdrop click
      const overlay = document.querySelector('[data-cart-drawer-overlay]');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeCartDrawer());
      }

      // Click outside drawer (on main content) - close drawer
      document.addEventListener('click', (e) => {
        if (!this.isOpen) return;
        
        // Check if click is outside the drawer
        const clickedInsideDrawer = this.drawer.contains(e.target);
        const clickedOnCartIcon = e.target.closest('[data-cart-icon]');
        
        // Close if clicking outside drawer and not on cart icon
        if (!clickedInsideDrawer && !clickedOnCartIcon) {
          this.closeCartDrawer();
        }
      }, true); // Use capture phase to catch early

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeCartDrawer();
        }
      });
    }

    /**
     * Setup global event listeners for cart updates
     */
    setupGlobalListeners() {
      // Listen for custom cart update events
      document.addEventListener('cart:updated', () => {
        this.updateCartDrawer().then(() => {
          this.openCartDrawer();
        });
      });

      // Listen for form submissions that add to cart
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form && form.action && form.action.includes('/cart/add')) {
          e.preventDefault();
          
          // Submit form via AJAX
          this.addToCart(form).then(() => {
            this.updateCartDrawer().then(() => {
              this.openCartDrawer();
            });
          }).catch((error) => {
            // Error adding to cart - silently fail
          });
        }
      }, true); // Use capture phase to catch early

      // Listen for Shopify's cart update events (if using Shopify's AJAX API)
      document.addEventListener('cart:refresh', () => {
        this.updateCartDrawer();
      });
    }

    /**
     * Open the cart drawer
     */
    openCartDrawer() {
      if (!this.drawer) return;
      
      // Always update cart data when opening
      this.updateCartDrawer().then(() => {
        this.drawer.classList.add('cart-drawer--visible');
        this.isOpen = true;
        this.setCartIconExpanded(true);
        
        // Prevent body scroll when drawer is open
        document.body.style.overflow = 'hidden';
        
        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:drawer:opened'));
      }).catch(() => {
        // Still open drawer even if update fails
        this.drawer.classList.add('cart-drawer--visible');
        this.isOpen = true;
        this.setCartIconExpanded(true);
        document.body.style.overflow = 'hidden';
      });
    }

    /**
     * Close the cart drawer
     */
    closeCartDrawer() {
      if (!this.drawer) return;
      
      this.drawer.classList.remove('cart-drawer--visible');
      this.isOpen = false;
      this.setCartIconExpanded(false);
      
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:drawer:closed'));
    }

    /**
     * Update aria-expanded state on all cart icon triggers
     */
    setCartIconExpanded(expanded) {
      if (!this.cartIcons || !this.cartIcons.length) return;
      const value = expanded ? 'true' : 'false';
      this.cartIcons.forEach((icon) => {
        try {
          icon.setAttribute('aria-expanded', value);
        } catch (e) {
          // ignore
        }
      });
    }

    /**
     * Update cart drawer content via AJAX
     * Fetches latest cart data from Shopify /cart endpoint
     */
    async updateCartDrawer() {
      if (!this.drawerTarget) return Promise.resolve();
      
      this.isUpdating = true;
      
      try {
        // Fetch cart data from Shopify
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cartData = await response.json();

        // Render cart from JSON data (more reliable than parsing HTML)
        this.renderCartFromData(cartData);

        // Update cart count in icons
        this.updateCartCount(cartData.item_count);

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:drawer:updated', {
          detail: { cart: cartData }
        }));

        return Promise.resolve();
        
      } catch (error) {
        this.drawerTarget.innerHTML = '<p class="cart-drawer__error">Error loading cart. Please try again.</p>';
        return Promise.reject(error);
      } finally {
        this.isUpdating = false;
      }
    }

    /**
     * Render cart from JSON data
     */
    renderCartFromData(cartData) {
      if (!this.drawerTarget) return;
      
      if (cartData.item_count === 0) {
        this.drawerTarget.innerHTML = `
          <div class="cart-drawer__empty">
            <p class="cart-drawer__empty-text">${this.escapeHtml('Your cart is empty')}</p>
            <a href="/" class="cart-drawer__continue-shopping">Continue Shopping</a>
          </div>
        `;
        return;
      }

      // Items list (scrollable)
      let itemsHtml = '<div class="cart-drawer__items">';
      
      cartData.items.forEach(item => {
        const imageUrl = item.image || '';
        const productUrl = item.url || '#';
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        const linePrice = this.formatMoney(item.line_price);
        const originalLinePrice = item.original_line_price !== item.line_price ? this.formatMoney(item.original_line_price) : null;
        
        itemsHtml += `
          <div class="cart-drawer__item" data-cart-item data-item-key="${this.escapeHtml(item.key)}">
            <a href="${this.escapeHtml(productUrl)}" class="cart-drawer__item-image">
              ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(item.product_title)}" loading="lazy" />` : '<div class="cart-drawer__item-placeholder"></div>'}
            </a>
            <div class="cart-drawer__item-details">
              <h3 class="cart-drawer__item-title">
                <a href="${this.escapeHtml(productUrl)}">${this.escapeHtml(item.product_title)}</a>
              </h3>
              ${variantTitle ? `<p class="cart-drawer__item-variant">${this.escapeHtml(variantTitle)}</p>` : ''}
              <div class="cart-drawer__item-price">
                ${originalLinePrice ? `<span class="cart-drawer__item-price--sale">${linePrice}</span><span class="cart-drawer__item-price--compare">${originalLinePrice}</span>` : `<span>${linePrice}</span>`}
              </div>
              <div class="cart-drawer__item-quantity">
                <span>Qty: ${item.quantity}</span>
              </div>
            </div>
          </div>
        `;
      });
      
      itemsHtml += '</div>';
      
      // Cart summary and actions (fixed footer)
      const footerHtml = `
        <div class="cart-drawer__footer">
          <div class="cart-drawer__summary">
            <div class="cart-drawer__subtotal">
              <span class="cart-drawer__subtotal-label">Subtotal</span>
              <span class="cart-drawer__subtotal-price">${this.formatMoney(cartData.total_price)}</span>
            </div>
            ${cartData.total_discount > 0 ? `
              <div class="cart-drawer__discount">
                <span>Discount</span>
                <span>-${this.formatMoney(cartData.total_discount)}</span>
              </div>
            ` : ''}
            <p class="cart-drawer__tax-note">Taxes and shipping calculated at checkout</p>
          </div>
          <div class="cart-drawer__actions">
            <a href="/checkout" class="cart-drawer__checkout-button">
              Checkout
            </a>
            <a href="/cart" class="cart-drawer__view-cart-button">
              View Cart
            </a>
          </div>
        </div>
      `;
      
      this.drawerTarget.innerHTML = itemsHtml + footerHtml;
      
      // Re-attach event listeners for remove buttons if needed
      this.attachCartItemListeners();
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Attach event listeners to cart items
     */
    attachCartItemListeners() {
      // Add any item-specific listeners here if needed
    }

    /**
     * Format money value
     */
    formatMoney(cents) {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      // Fallback formatting
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }

    /**
     * Update cart count in icons
     */
    updateCartCount(count) {
      const countElements = document.querySelectorAll('[data-cart-count]');
      countElements.forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    /**
     * Validate quantity input
     */
    validateQuantity(quantity) {
      const qty = parseInt(quantity, 10);
      if (isNaN(qty) || qty < 1) {
        return { valid: false, error: 'Quantity must be at least 1' };
      }
      if (qty > 999) {
        return { valid: false, error: 'Quantity cannot exceed 999' };
      }
      return { valid: true, value: qty };
    }

    /**
     * Add product to cart via AJAX
     */
    async addToCart(form) {
      // Validate form inputs
      const formData = new FormData(form);
      const quantity = formData.get('quantity');
      
      if (quantity) {
        const validation = this.validateQuantity(quantity);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
        // Ensure quantity is set to validated value
        formData.set('quantity', validation.value);
      }

      // Validate variant ID
      const variantId = formData.get('id');
      if (!variantId) {
        throw new Error('Product variant is required');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      const data = await response.json();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:added', {
        detail: { item: data }
      }));

      return data;
    }
  }

  // Initialize cart drawer when script loads
  window.CartDrawer = CartDrawer;
  
  // Auto-initialize if DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartDrawer = new CartDrawer();
    });
  } else {
    window.cartDrawer = new CartDrawer();
  }

  // Export functions for global use
  window.openCartDrawer = function() {
    if (window.cartDrawer) {
      window.cartDrawer.openCartDrawer();
    }
  };

  window.updateCartDrawer = function() {
    if (window.cartDrawer) {
      return window.cartDrawer.updateCartDrawer();
    }
    return Promise.resolve();
  };

})();

